import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, AlertTitle, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// routes
import { PATH_AUTH, PATH_PAGE } from '../../../routes/paths';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
// utils
import { setSession } from '../../../utils/jwt';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  // const [userEmail, setUserEmail] = useState('');

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    const response = await login(data.email, data.password);

    if (!response.success) {
      const userEmail = window.localStorage.getItem('userEmail') || 'your email address';

      if (response.message && response.message === 'Invalid credentials') {
        setError('invalidCredentials', { type: 'Error', message: 'Invalid email or password' });
      } else if (response.message && response.message === 'This user has not yet been verified.') {
        setError('notVerified', {
          type: 'Error',
          message: `Please verify your email. We have sent the verification mail to ${userEmail}. If you cannot find the email in the inbox folder, please check your Junk/Spam folder.`,
        });
      }else{
        setError('afterSubmit', { type: 'Error', message: response.message });
      }
      return;
    }
    reset();
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        {!!errors.invalidCredentials && <Alert severity="error">{errors.invalidCredentials.message}</Alert>}
        {!!errors.notVerified && (
          <Alert severity="error">
            <AlertTitle>Your email is not verified!</AlertTitle>
            {errors.notVerified.message}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Still didn't find the email? request a new verification email.{' '}
              <Link component={RouterLink} to={PATH_AUTH.verifyResendVerification}>
                Resend verification email
              </Link>
            </Typography>
          </Alert>
        )}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        {/* <RHFCheckbox name="remember" label="Remember me" /> */}
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
        Login
      </LoadingButton>
    </FormProvider>
  );
}
